import { useCallback, useEffect, useRef, useState } from 'react';
import type { CueScript, RoomCredentials } from '../types';
import type { TakeEntry } from '../studio/useTakeJournal';
import { createProductionDocument, editProductionDocument, mergeProductionDocuments, validProductionDocument, saveProductionDocument, NOTES_STORAGE_PREFIX, NOTES_ID_RE, type ProductionDocument, type NoteValue } from './model';
import { createNotesTransport, ensureNotesCredentials, validNotesCredentials, type NotesCredentials, type NotesTransport, type NotesTransportStatus } from './transport';
import { noteText, noteNumber, type ProductionNotesController } from './controller';

const ACTIVE = 'cue-studio-production-project-v1';
const SHARING = 'cue-studio-production-sharing-v1';
const emptyStatus: NotesTransportStatus = { connected: false, error: null, peerCount: 0 };
export function notesRecordId(value: string): string {
  if (NOTES_ID_RE.test(value)) return value;
  // Stable IDs let old imports with short, long or non-ASCII IDs converge too.
  let hash = 0x6c62272e07bb014262b821756295c58dn;
  for (const byte of new TextEncoder().encode(value)) hash = BigInt.asUintN(128, (hash ^ BigInt(byte)) * 0x0000000001000000000000000000013bn);
  return `legacy_${hash.toString(16).padStart(32, '0')}`;
}
export function readNotesLink(): NotesCredentials | null {
  try {
    const raw = new URLSearchParams(location.hash.slice(1)).get('notes');
    const value: unknown = raw ? JSON.parse(raw) : null;
    return validNotesCredentials(value) ? { roomId: value.roomId, notesToken: value.notesToken, projectId: value.projectId, ...(value.relayUrl ? { relayUrl: value.relayUrl } : {}) } : null;
  } catch { return null; }
}
export function notesLink(credentials: NotesCredentials): string {
  const url = new URL(location.pathname, location.origin);
  url.searchParams.set('view', 'notes');
  url.hash = new URLSearchParams({ notes: JSON.stringify(credentials) }).toString();
  return url.href;
}
function download(text: string, name: string, type: string) {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const a = window.document.createElement('a'); a.href = url; a.download = name; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 30000);
}
export function notesCsv(doc: ProductionDocument): string {
  const custom = Object.entries(doc.fields).filter(([key, f]) => key.startsWith('custom_') && noteText(f.value));
  const keys = ['title', 'takeNumber', 'rating', 'talent', 'scene', 'camera', 'clip', 'timecode', 'notes', 'startedAt', 'duration', ...custom.map(([key]) => key)];
  const escape = (v: unknown) => { const s = String(v ?? ''); return '"' + (/^[\s\u0000-\u001f]*[=+\-@]/u.test(s) ? "'" + s : s).replaceAll('"', '""') + '"'; };
  return '\ufeff' + [keys.map((key) => escape(doc.fields[key]?.value || key)).join(','), ...Object.values(doc.records)
    .filter(r => r.fields.kind?.value === 'take' && r.fields.deleted?.value !== true)
    .sort((a,b) => noteNumber(a.fields.startedAt?.value) - noteNumber(b.fields.startedAt?.value))
    .map(r => keys.map(k => { const v = r.fields[k]?.value; return escape(k === 'startedAt' && typeof v === 'number' && Math.abs(v) <= 8.64e15 ? new Date(v).toISOString() : v); }).join(','))].join('\r\n');
}
export function remapNotesForBackup(source: ProductionDocument, target: ProductionDocument, actor: string, scripts: Map<string,string>, takes: Map<string,string>): ProductionDocument {
  const records: Record<string, Record<string, NoteValue>> = {};
  for (const [id, record] of Object.entries(source.records)) {
    const fields = Object.fromEntries(Object.entries(record.fields).map(([k,r]) => [k,r.value]));
    const oldScript = noteText(fields.scriptId);
    fields.scriptId = scripts.get(oldScript) || oldScript;
    const oldTake = noteText(fields.sourceTakeId);
    if (oldTake) fields.sourceTakeId = takes.get(oldTake) || oldTake;
    const nextId = fields.kind === 'script' && fields.scriptId ? notesRecordId(`script_${fields.scriptId}`) : takes.get(id) || crypto.randomUUID();
    records[nextId] = fields;
  }
  const fields = Object.fromEntries(Object.entries(source.fields).map(([k,r]) => [k,r.value]));
  return editProductionDocument(target, actor, { fields, records });
}

interface Options { enabled?: boolean; credentials?: RoomCredentials | null; invitation?: NotesCredentials | null; scripts?: CueScript[]; takes?: TakeEntry[]; standalone?: boolean }
export function useProductionNotes(options: Options = {}): ProductionNotesController & {
  getDocument(): ProductionDocument | null;
  prepareRestore(source: ProductionDocument, scripts: Map<string,string>, takes: Map<string,string>): ProductionDocument;
  restoreDocument(doc: ProductionDocument, options?: { persisted?: boolean }): void;
} {
  const { enabled = true, credentials, invitation, scripts, takes, standalone = false } = options;
  const actor = useRef(crypto.randomUUID()).current;
  const [initial] = useState(() => {
    let projectId = invitation?.projectId || crypto.randomUUID(); let error: string | null = null; let doc: ProductionDocument | null = null;
    if (!enabled) return { projectId, doc, error };
    try {
      if (!standalone) {
        const saved = localStorage.getItem(ACTIVE);
        if (saved && NOTES_ID_RE.test(saved)) projectId = saved;
        else localStorage.setItem(ACTIVE, projectId);
      }
      const raw = localStorage.getItem(NOTES_STORAGE_PREFIX + projectId);
      if (raw) { const value: unknown = JSON.parse(raw); if (!validProductionDocument(value) || value.projectId !== projectId) throw new Error('Saved editor notes could not be read. Export a backup before clearing browser storage.'); doc = value; }
      if (!doc && !standalone) { doc = createProductionDocument(projectId, 'My production', actor); saveProductionDocument(doc); }
    } catch (e) { error = e instanceof Error ? e.message : 'Editor notes storage is unavailable.'; }
    return { projectId, doc, error };
  });
  const [document, setDocument] = useState(initial.doc);
  const ref = useRef(document);
  const [storageError, setStorageError] = useState(initial.error);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<NotesTransportStatus>(emptyStatus);
  const [access, setAccess] = useState<NotesCredentials | null>(invitation || null);
  const [sharing, setSharing] = useState(() => { try { return localStorage.getItem(SHARING) === '1'; } catch { return false; } });
  const transport = useRef<NotesTransport | null>(null);
  const projectId = initial.projectId;
  const accept = useCallback((next: ProductionDocument, publish = true, persisted = false) => {
    if (next.projectId !== projectId || !validProductionDocument(next)) throw new Error('These notes belong to a different project.');
    if (JSON.stringify(next) === JSON.stringify(ref.current)) return;
    ref.current = next; setDocument(next);
    try { if (!persisted) saveProductionDocument(next); setStorageError(null); }
    catch { setStorageError('Changes are only in memory: device storage is full or unavailable. Export this project before closing.'); }
    if (publish) transport.current?.publish(next);
  }, [projectId]);
  const edit = useCallback((changes: { fields?: Record<string, NoteValue>; records?: Record<string, Record<string, NoteValue>> }) => {
    try {
      if (!ref.current) throw new Error('Open this link while the operator is connected to load the project first.');
      accept(editProductionDocument(ref.current, actor, changes)); setError(null);
    } catch (e) { setError(e instanceof Error ? e.message : 'This edit could not be saved.'); }
  }, [accept, actor]);
  const prepareShare = useCallback(async () => {
    if (!enabled) throw new Error('Editor notes are available from the operator desk.');
    if (standalone && access) return notesLink(access);
    if (!credentials) throw new Error('The sharing room is still connecting. Try again in a moment.');
    setSharing(true);
    try { localStorage.setItem(SHARING, '1'); } catch { /* Notes saving reports storage health separately. */ }
    const next = await ensureNotesCredentials(credentials, projectId); setAccess(next); return notesLink(next);
  }, [enabled, standalone, access, credentials, projectId]);
  useEffect(() => {
    if (!enabled || standalone || !credentials || !sharing) return;
    let active = true;
    const connect = () => { void ensureNotesCredentials(credentials, projectId).then(c => { if (active) { setAccess(c); setError(null); } }).catch(e => { if (active) setError(e instanceof Error ? e.message : 'Sharing is unavailable. Notes still save on this device.'); }); };
    connect(); window.addEventListener('online', connect);
    return () => { active = false; window.removeEventListener('online', connect); };
  }, [enabled, standalone, credentials, projectId, sharing]);
  useEffect(() => {
    if (!enabled || !access) return;
    const connection = createNotesTransport(access, { onDocument: incoming => {
      try { accept(ref.current ? mergeProductionDocuments(ref.current, incoming) : incoming, false); }
      catch(e) { setError(e instanceof Error ? e.message : 'Shared notes could not merge.'); }
    }, onStatus: setStatus });
    transport.current = connection;
    if (ref.current) connection.publish(ref.current);
    return () => { connection.close(); transport.current = null; };
  }, [enabled, access, accept]);
  useEffect(() => {
    if (!enabled) return;
    const onStorage = (event: StorageEvent) => {
      if (event.key !== NOTES_STORAGE_PREFIX + projectId || !event.newValue) return;
      try { const next: unknown = JSON.parse(event.newValue); if (validProductionDocument(next)) accept(ref.current ? mergeProductionDocuments(ref.current, next) : next); } catch { /* Keep the intact in-memory document. */ }
    };
    window.addEventListener('storage', onStorage); return () => window.removeEventListener('storage', onStorage);
  }, [enabled, projectId, accept]);
  useEffect(() => {
    if (!enabled || standalone || !ref.current) return;
    const records: Record<string, Record<string, NoteValue>> = {};
    for (const script of scripts || []) {
      const id = notesRecordId(`script_${script.id}`); const old = ref.current.records[id];
      if (!old || old.fields.title?.value !== script.title) records[id] = { kind: 'script', scriptId: script.id, title: script.title };
    }
    const existing = new Set(Object.values(ref.current.records).map(r => r.fields.sourceTakeId?.value));
    for (const take of [...(takes || [])].reverse()) {
      if (existing.has(take.id)) continue;
      const count = Object.values(ref.current.records).filter(r => r.fields.kind?.value === 'take' && r.fields.scriptId?.value === take.scriptId).length + Object.values(records).filter(r => r.kind === 'take' && r.scriptId === take.scriptId).length;
      records[notesRecordId(take.id)] = { kind: 'take', sourceTakeId: take.id, scriptId: take.scriptId, title: take.title, takeNumber: count + 1, talent: noteText(ref.current.fields.defaultTalent?.value), rating: 'unrated', startedAt: take.startedAt, duration: take.duration, notes: take.notes, mode: take.mode };
    }
    if (Object.keys(records).length) edit({ records });
  }, [enabled, standalone, scripts, takes, edit]);

  const addTake = (scriptId = '') => {
    const id = crypto.randomUUID(); const doc = ref.current;
    if (!doc) return '';
    const script = Object.values(doc.records).find(r => r.fields.kind?.value === 'script' && r.fields.scriptId?.value === scriptId);
    const numbers = Object.values(doc.records).filter(r => r.fields.kind?.value === 'take' && r.fields.scriptId?.value === scriptId).map(r => noteNumber(r.fields.takeNumber?.value));
    edit({ records: { [id]: { kind: 'take', scriptId, title: noteText(script?.fields.title?.value, 'Unassigned take'), takeNumber: Math.max(0, ...numbers) + 1, talent: noteText(doc.fields.defaultTalent?.value), rating: 'unrated', startedAt: Date.now(), notes: '' } } });
    return ref.current?.records[id] ? id : '';
  };
  const exportProject = (format: 'json' | 'csv') => {
    if (!ref.current) return;
    const content = format === 'csv' ? notesCsv(ref.current) : JSON.stringify({ format: 'cue-studio-production-notes', version: 1, document: ref.current }, null, 2);
    download(content, `cue-studio-editor-notes.${format}`, format === 'csv' ? 'text/csv;charset=utf-8' : 'application/json');
  };
  const importProject = async (file: File) => {
    try {
      if (file.size > 1024 * 1024) throw new Error('Use an editor-notes project smaller than 1 MB.');
      const value = JSON.parse(await file.text());
      if (value.format !== 'cue-studio-production-notes' || !validProductionDocument(value.document)) throw new Error('Choose a Cue Studio editor-notes project export.');
      if (value.document.projectId !== projectId) throw new Error('This file belongs to a different project. Open its original project link to merge these notes.');
      accept(ref.current ? mergeProductionDocuments(ref.current, value.document) : value.document); setError(null);
    } catch(e) { setError(e instanceof Error ? e.message : 'Project import failed.'); throw e; }
  };
  return { document, projectId, storageError, error, status, shareUrl: access ? notesLink(access) : '',
    setProjectFields: fields => edit({ fields }), setRecordFields: (id, fields) => edit({ records: { [id]: fields } }), addTake,
    deleteTake: id => edit({ records: { [id]: { deleted: true } } }), exportProject, importProject, prepareShare,
    getDocument: () => ref.current,
    prepareRestore: (source, scriptsMap, takesMap) => {
      if (!ref.current) throw new Error('Editor notes storage is unavailable.');
      return remapNotesForBackup(source, ref.current, actor, scriptsMap, takesMap);
    },
    restoreDocument: (doc, options) => accept(doc, true, options?.persisted === true),
  };
}
